import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Spinner } from '../components/ui/spinner';
import { AlertCircle, Baby, Calendar, CheckCircle, Clock, Edit2, MapPin, User, Users } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';
import { getBasicAgeRequirementDescription } from '../utils/eventValidation';
import { MemberCard } from '../components/MemberCard';
import { EditableFamilyDetails } from '../components/EditableFamilyDetails';
import { EditableAdultCard, EditableChildCard } from '../components/EditableMemberCard';
import { AddAdultCard } from '../components/AddAdultCard';
import { AddChildCard } from '../components/AddChildCard';
import { useFamilyMembers } from '../hooks/useFamilyMembers';
import { useMemberSelection } from '../hooks/useMemberSelection';
import { useEventNavigation } from '../hooks/useEventNavigation';

import { useTranslation } from 'react-i18next';
import { formatFamilyName } from '../utils/family.utils';

export const FamilyPage = () => {
  const { t } = useTranslation(['registration', 'common']);
  const { id: familyId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(searchParams.get('edit') === 'true');
  const { selectedEvent, navigateNext } = useEventNavigation();
  const { family, loading, error, allMembers, eligibleMembers, ineligibleMembers } = useFamilyMembers(
    selectedEvent,
    familyId,
  );

  const { selectedMembers, selectedCount, selectionButtons, handleMemberToggle, isSelected } = useMemberSelection(
    eligibleMembers,
    selectedEvent,
  );

  // Determine if we're in event selection mode (selecting members for an event)
  const isEventSelectionMode = !!selectedEvent;

  const handleContinue = () => {
    if (selectedCount === 0) return;
    const memberDetails = allMembers.filter((member) => selectedMembers.includes(member.id));
    navigateNext(selectedMembers, memberDetails);
  };

  const isMemberEligible = (memberId: string): boolean => {
    return eligibleMembers.some((member) => member.id === memberId);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Spinner className="h-12 w-12 mx-auto mb-4" />
            <p className="text-[var(--color-neutral-700)]">{t('common:families.loadingFamily')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !family) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-8">
          <Alert className="border-[var(--color-error-500)]">
            <AlertCircle className="h-5 w-5" />
            <AlertDescription>
              {error}
              <Button variant="outline" size="sm" onClick={() => window.location.reload()} className="mt-3 w-full">
                Try Again
              </Button>
            </AlertDescription>
          </Alert>
        </div>
      </Layout>
    );
  }

  const adults = allMembers.filter((member) => member.type === 'adult');
  const children = allMembers.filter((member) => member.type === 'child');

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6">
        <div className="mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-[var(--color-neutral-900)] mb-2">
                {formatFamilyName(family.name, t, 'common:families.familyName')}
              </h1>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:gap-4 text-sm text-[var(--color-neutral-700)]">
                <span className="flex items-center gap-1">
                  <Users className="h-4 w-4" />
                  {t('profile.members', { count: family.demographics.totalMembers })}
                </span>
                <span>•</span>
                <span>{t('profile.adultsCount', { count: family.demographics.adultCount })}</span>
                <span>•</span>
                <span>{t('profile.childrenCount', { count: family.demographics.childCount })}</span>
              </div>
            </div>
            {!isEventSelectionMode && (
              <Button
                variant={isEditMode ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  if (isEditMode) {
                    navigate('/my-families');
                  } else {
                    setIsEditMode(true);
                  }
                }}
                className="shrink-0"
              >
                <Edit2 className="h-4 w-4 mr-1.5" />
                {isEditMode ? t('profile.done') : t('profile.edit')}
              </Button>
            )}
          </div>
        </div>

        {selectedEvent && (
          <Alert className="mb-6 border-[var(--color-secondary-300)] bg-[var(--color-secondary-100)]">
            <Calendar className="h-4 w-4" />
            <AlertDescription>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <p className="font-medium text-[var(--color-neutral-900)]">{selectedEvent.name}</p>
                  <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-[var(--color-neutral-700)]">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(selectedEvent.startDateTime).toLocaleDateString()}
                    </span>
                    {selectedEvent.location && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {selectedEvent.location}
                      </span>
                    )}
                    <Badge variant="outline" className="text-xs">
                      {getBasicAgeRequirementDescription(selectedEvent)}
                    </Badge>
                  </div>
                </div>
                {ineligibleMembers.length > 0 && (
                  <Badge variant="warning" className="md:ml-auto">
                    {t('profile.ineligible', { count: ineligibleMembers.length })}
                  </Badge>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {isEditMode && !isEventSelectionMode && <EditableFamilyDetails family={family} />}

        <div className="space-y-6">
          {!isEditMode && (
            <div className="bg-[var(--color-brand-white)] sticky top-0 z-10 py-3 px-4 rounded-lg border shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <p className="font-medium text-[var(--color-neutral-900)]">
                    {selectedEvent ? t('profile.selectAttendees') : t('profile.yourFamilyMembers')}
                  </p>
                  <p className="text-sm text-[var(--color-neutral-700)]">
                    {selectedCount > 0 ? t('profile.selected', { count: selectedCount }) : t('profile.clickToSelect')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectionButtons.find((b) => b.key === 'selectAll')?.action}
                    disabled={selectionButtons.find((b) => b.key === 'selectAll')?.disabled}
                  >
                    {t('profile.selectAll')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectionButtons.find((b) => b.key === 'clearAll')?.action}
                    disabled={selectionButtons.find((b) => b.key === 'clearAll')?.disabled}
                  >
                    {t('profile.clear')}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {adults.length > 0 && (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-medium text-[var(--color-neutral-700)] mb-3">
                <User className="h-4 w-4" />
                {t('profile.adultsSection', { count: adults.length })}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {isEditMode && !isEventSelectionMode ? (
                  <>
                    {[family.primaryAdult, ...family.adults.filter((a) => a.id !== family.primaryAdult.id)].map(
                      (adult) => (
                        <EditableAdultCard
                          key={adult.id}
                          adult={adult}
                          familyId={familyId!}
                          isPrimary={adult.id === family.primaryAdult.id}
                          totalAdults={adults.length}
                          children={family.children}
                          relationships={family.relationships}
                          familyStatus={family.status}
                        />
                      ),
                    )}
                    {adults.length < 10 && (
                      <AddAdultCard familyId={familyId!} children={family.children} familyStatus={family.status} />
                    )}
                  </>
                ) : (
                  adults.map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      isSelected={isSelected(member.id)}
                      isEligible={isMemberEligible(member.id)}
                      onToggle={handleMemberToggle}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {(children.length > 0 || (isEditMode && !isEventSelectionMode)) && (
            <div>
              <h3 className="flex items-center gap-2 text-sm font-medium text-[var(--color-neutral-700)] mb-3">
                <Baby className="h-4 w-4" />
                {t('profile.childrenSection', { count: children.length })}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                {isEditMode && !isEventSelectionMode ? (
                  <>
                    {family.children.map((child) => (
                      <EditableChildCard
                        key={child.id}
                        child={child}
                        familyId={familyId!}
                        familyStatus={family.status}
                      />
                    ))}
                    {children.length < 10 && <AddChildCard familyId={familyId!} familyStatus={family.status} />}
                  </>
                ) : (
                  children.map((member) => (
                    <MemberCard
                      key={member.id}
                      member={member}
                      isSelected={isSelected(member.id)}
                      isEligible={isMemberEligible(member.id)}
                      onToggle={handleMemberToggle}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {selectedCount > 0 && !isEditMode && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle className="h-5 w-5 text-[var(--color-success-700)]" />
                  {t('profile.selected', { count: selectedCount })}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
                  {allMembers
                    .filter((member) => selectedMembers.includes(member.id))
                    .map((member) => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between py-2 px-3 bg-[var(--color-neutral-300)]/20 rounded"
                      >
                        <span className="truncate">
                          {member.firstName} {member.lastName}
                        </span>
                        <button
                          onClick={() => handleMemberToggle(member.id)}
                          className="text-xs text-[var(--color-neutral-400)] hover:text-[var(--color-error-700)]"
                        >
                          {t('profile.remove')}
                        </button>
                      </div>
                    ))}
                </div>
                <Button
                  variant="default"
                  className="w-full"
                  size="lg"
                  onClick={handleContinue}
                  disabled={selectedCount === 0}
                >
                  {t('buttons.continue')}
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};
